import React, { useState, useEffect } from 'react';
import {
  Search, Filter, Eye, CheckCircle, XCircle, Clock,
  Calendar, Phone, Mail, User, Baby, AlertCircle
} from 'lucide-react';
import crecheApi from '../../services/crecheApi';

export default function PreInscricoesAdminPage() {
  const [preInscricoes, setPreInscricoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('AGUARDANDO_PAGAMENTO');
  const [selectedInscricao, setSelectedInscricao] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    aguardando: 0,
    aprovadas: 0,
    rejeitadas: 0
  });

  useEffect(() => {
    carregarPreInscricoes();
  }, [statusFilter]);

  const carregarPreInscricoes = async () => {
    setLoading(true);
    try {
      const data = await crecheApi.listarPreInscricoes();
      const inscricoes = Array.isArray(data) ? data : [];
      
      setPreInscricoes(inscricoes);
      
      setStats({
        total: inscricoes.length,
        aguardando: inscricoes.filter(i => i.status === 'AGUARDANDO_PAGAMENTO').length,
        aprovadas: inscricoes.filter(i => i.status === 'ATIVA').length,
        rejeitadas: inscricoes.filter(i => i.status === 'REJEITADA').length
      });
    } catch (err) {
      console.error('Erro ao carregar pré-inscrições:', err);
    } finally {
      setLoading(false);
    }
  };

  const visualizarDetalhes = async (inscricao) => {
    try {
      const detalhes = await crecheApi.detalharPreInscricao(inscricao.id);
      setSelectedInscricao(detalhes || inscricao);
      setShowModal(true);
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      setSelectedInscricao(inscricao);
      setShowModal(true);
    }
  };

  const atualizarStatus = async (id, novoStatus, motivo = '') => {
    if (!window.confirm(`Confirma ${novoStatus === 'ATIVA' ? 'APROVAÇÃO' : 'REJEIÇÃO'} desta pré-inscrição?`)) {
      return;
    }

    setActionLoading(true);
    try {
      await crecheApi.atualizarStatusPreInscricao(id, novoStatus, motivo);
      setShowModal(false);
      carregarPreInscricoes();
      alert(`Pré-inscrição ${novoStatus === 'ATIVA' ? 'aprovada' : 'rejeitada'} com sucesso!`);
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao atualizar status');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredInscricoes = preInscricoes.filter(insc => {
    const matchSearch = 
      insc.nomeCrianca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insc.nomeResponsavel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insc.emailResponsavel?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatus = !statusFilter || insc.status === statusFilter;
    
    return matchSearch && matchStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      AGUARDANDO_PAGAMENTO: 'bg-yellow-100 text-yellow-800',
      ATIVA: 'bg-green-100 text-green-800',
      REJEITADA: 'bg-red-100 text-red-800',
      INATIVA: 'bg-gray-100 text-gray-800'
    };
    
    const labels = {
      AGUARDANDO_PAGAMENTO: 'Aguardando',
      ATIVA: 'Aprovada',
      REJEITADA: 'Rejeitada',
      INATIVA: 'Inativa'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.INATIVA}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Gestão de Pré-inscrições
          </h1>
          <p className="text-gray-600">
            Gerencie e aprove inscrições de novos alunos
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <User className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aguardando</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.aguardando}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aprovadas</p>
                <p className="text-2xl font-bold text-green-600">{stats.aprovadas}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Rejeitadas</p>
                <p className="text-2xl font-bold text-red-600">{stats.rejeitadas}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os Status</option>
                <option value="AGUARDANDO_PAGAMENTO">Aguardando Pagamento</option>
                <option value="ATIVA">Aprovadas</option>
                <option value="REJEITADA">Rejeitadas</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Carregando...</p>
            </div>
          ) : filteredInscricoes.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600">Nenhuma pré-inscrição encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criança
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Responsável
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInscricoes.map((insc) => (
                    <tr key={insc.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Baby className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {insc.nomeCrianca || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {insc.idadeCrianca ? `${insc.idadeCrianca} anos` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{insc.nomeResponsavel || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{insc.parentesco || ''}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Mail className="h-4 w-4 mr-1 text-gray-400" />
                          {insc.emailResponsavel || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <Phone className="h-4 w-4 mr-1 text-gray-400" />
                          {insc.telefoneResponsavel || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                          {insc.dataInscricao ? new Date(insc.dataInscricao).toLocaleDateString('pt-PT') : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(insc.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => visualizarDetalhes(insc)}
                          className="text-blue-600 hover:text-blue-900 flex items-center justify-end ml-auto"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver Detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && selectedInscricao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Detalhes da Pré-inscrição</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Baby className="h-5 w-5 mr-2 text-blue-600" />
                  Dados da Criança
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Nome Completo</p>
                      <p className="font-medium text-gray-900">{selectedInscricao.nomeCrianca || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Data de Nascimento</p>
                      <p className="font-medium text-gray-900">
                        {selectedInscricao.dataNascimento 
                          ? new Date(selectedInscricao.dataNascimento).toLocaleDateString('pt-PT')
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Género</p>
                      <p className="font-medium text-gray-900">
                        {selectedInscricao.genero === 'M' ? 'Masculino' : selectedInscricao.genero === 'F' ? 'Feminino' : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Grupo Atribuído</p>
                      <p className="font-medium text-gray-900">{selectedInscricao.nomeGrupo || 'Não atribuído'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-600" />
                  Dados do Responsável
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Nome</p>
                      <p className="font-medium text-gray-900">{selectedInscricao.nomeResponsavel || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Parentesco</p>
                      <p className="font-medium text-gray-900">{selectedInscricao.parentesco || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium text-gray-900">{selectedInscricao.emailResponsavel || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Telefone</p>
                      <p className="font-medium text-gray-900">{selectedInscricao.telefoneResponsavel || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Status Atual</h3>
                <div className="flex items-center space-x-3">
                  {getStatusBadge(selectedInscricao.status)}
                  <p className="text-sm text-gray-600">
                    Inscrito em: {selectedInscricao.dataInscricao 
                      ? new Date(selectedInscricao.dataInscricao).toLocaleDateString('pt-PT')
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {selectedInscricao.status === 'AGUARDANDO_PAGAMENTO' && (
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => atualizarStatus(selectedInscricao.id, 'ATIVA')}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center"
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    {actionLoading ? 'Processando...' : 'Aprovar Inscrição'}
                  </button>
                  <button
                    onClick={() => atualizarStatus(selectedInscricao.id, 'REJEITADA', 'Documentação incompleta')}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center"
                  >
                    <XCircle className="h-5 w-5 mr-2" />
                    {actionLoading ? 'Processando...' : 'Rejeitar Inscrição'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}